import React, { useEffect, useRef, useCallback } from 'react';
import SurveyItemDetail from './SurveyItemDetail';

/**
 * Container component for displaying multiple survey items with infinite scroll
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of survey items to display
 * @param {string} props.selectedItemId - Currently selected item ID
 * @param {Function} props.setSelectedItemId - Function to set the selected item ID
 * @param {Function} props.updateItem - Function to update an item
 */
const SurveyItemDetailView = ({ items, selectedItemId, setSelectedItemId, updateItem }) => {
  const scrollContainerRef = useRef(null);
  const itemRefs = useRef({});
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const lastScrollTimeRef = useRef(0);
  
  // Add a debounce timer to avoid changing selection during fast scrolling
  const lastSelectionTimeRef = useRef(0);
  const isScrollSelectionRef = useRef(false); // Track if selection changes from scrolling
  const SELECTION_DEBOUNCE_MS = 400; // Minimum time between selection changes

  // Calculate the selected item index for future use
  // const selectedItemIndex = items.findIndex(item => item.refId === selectedItemId);
  
  // Handle user scroll events to track when user is actively scrolling
  const handleUserScrollStart = useCallback(() => {
    // Update the last scroll time
    lastScrollTimeRef.current = Date.now();
    
    // Clear any existing timeout and set ref to null to avoid stale references
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  // Add a throttled scroll handler to detect the current item
  const handleScroll = useCallback(() => {
    // Skip processing if programmatic scrolling is happening
    if (isScrollingRef.current) return;

    // Find the item that is most visible in the viewport
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;
    const viewportHeight = containerRect.height;
    
    // Calculate the position about 1/2 way down the visible area, accounting for header
    // This creates a more intentional selection that won't change too early
    const headerHeight = 65; // Approximate height of our header
    const adjustedContainerTop = containerTop + headerHeight;
    const focusPosition = adjustedContainerTop + ((viewportHeight - headerHeight) / 2);
    
    // Find which item intersects with the focus position
    let closestItem = null;
    let closestDistance = Infinity;
    
    Object.entries(itemRefs.current).forEach(([itemId, itemRef]) => {
      if (!itemRef) return;
      
      const itemRect = itemRef.getBoundingClientRect();
      
      // Check if the item contains the focus position
      if (itemRect.top <= focusPosition && itemRect.bottom >= focusPosition) {
        closestItem = itemId;
        return; // This item contains the focus position, so it's perfect
      }
      
      // If we don't have a direct hit, calculate distance to focus position
      const topDistance = Math.abs(itemRect.top - focusPosition);
      const bottomDistance = Math.abs(itemRect.bottom - focusPosition);
      const minDistance = Math.min(topDistance, bottomDistance);
      
      if (minDistance < closestDistance) {
        closestDistance = minDistance;
        closestItem = itemId;
      }
    });
    
    // Update the selected item if we found a closest item AND it's significantly visible
    if (closestItem && closestItem !== selectedItemId) {
      // Calculate the visibility of the closest item
      const itemRect = itemRefs.current[closestItem].getBoundingClientRect();
      const itemHeight = itemRect.height;
      const visibleTop = Math.max(itemRect.top, adjustedContainerTop);
      const visibleBottom = Math.min(itemRect.bottom, adjustedContainerTop + viewportHeight - headerHeight);
      const visibleHeight = visibleBottom > visibleTop ? (visibleBottom - visibleTop) : 0;
      const visibilityRatio = visibleHeight / itemHeight;
      
      // Check if we're in the debounce period
      const now = Date.now();
      const timeSinceLastSelection = now - lastSelectionTimeRef.current;
      
      // Only change selection if we're past the debounce period and item is very visible
      if (timeSinceLastSelection > SELECTION_DEBOUNCE_MS && visibilityRatio > 0.65) {
        // Update our last selection time
        lastSelectionTimeRef.current = now;
        
        // Flag that this is a scroll-based selection
        isScrollSelectionRef.current = true;
        
        // Set the new selected item
        setSelectedItemId(closestItem);
        
        // Reset the scroll selection flag after a delay
        const selectionFlagTimeout = setTimeout(() => {
          isScrollSelectionRef.current = false;
        }, 500);
        
        // Store the timeout ID for cleanup
        return () => clearTimeout(selectionFlagTimeout);
      }
    }
  }, [selectedItemId, setSelectedItemId]);

  // Set up the scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    // Use a throttled version of the scroll handler for better performance
    let scrollTimeout;
    const throttledScrollHandler = () => {
      if (scrollTimeout) return;
      
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 150); // Longer throttle delay to prevent flickering during rapid scrolling
    };
    
    container.addEventListener('scroll', throttledScrollHandler, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [handleScroll]);
  
  // Scroll to the selected item when it changes from external source
  // BUT ONLY when selection is made from grid, NOT during normal scrolling
  useEffect(() => {
    let gridScrollTimeout;
    
    if (selectedItemId && itemRefs.current[selectedItemId] && scrollContainerRef.current) {
      // Check if the user was scrolling recently
      const now = Date.now();
      // const isRecentScroll = (now - lastScrollTimeRef.current) < 300;
      
      // IMPORTANT: Only scroll if this is from a grid selection, not during scrolling
      const isFromGrid = window.isSelectingFromGrid || false;
      const isScrollSelection = isScrollSelectionRef.current;
      const isScrolling = now - lastScrollTimeRef.current < 300;
      
      // Only scroll for explicit grid selections
      if (isFromGrid && !isScrolling && !isScrollSelection) {
        isScrollingRef.current = true;
        
        // Immediate jump with no animation when clicking in grid
        const container = scrollContainerRef.current;
        const targetTop = itemRefs.current[selectedItemId].offsetTop;
        container.scrollTop = targetTop;
        
        // Reset the scrolling flag after a brief delay
        gridScrollTimeout = setTimeout(() => {
          isScrollingRef.current = false;
          window.isSelectingFromGrid = false;
        }, 200);
      }
    }
    
    // Clean up any timeout we created in this effect
    return () => {
      if (gridScrollTimeout) {
        clearTimeout(gridScrollTimeout);
      }
    };
  }, [selectedItemId]);
  
  // Manually clean up scrollTimeoutRef on component unmount
  useEffect(() => {
    // Store the cleanup function
    return () => {
      // No need to capture the ref value here since we're just cleaning up resources
      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, []);
  
  // If we have no items, show a message
  if (items.length === 0) {
    return <div className="p-4 text-center text-gray-500">No survey items available</div>;
  }
  
  // Find the current item for display in the header
  const currentItem = selectedItemId ? items.find(item => item.refId === selectedItemId) : null;
  
  // Function to get color for header bar if needed - same as in SurveyItemCard
  function getColorForId(id) {
    if (!id) return 'bg-gray-500';
    
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500', 
      'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash << 5) - hash + id.charCodeAt(i);
      hash = hash & hash;
    }
    
    return colors[Math.abs(hash) % colors.length];
  }
  
  return (
    <div 
      ref={scrollContainerRef}
      className="h-full overflow-auto"
      style={{ scrollbarWidth: 'thin' }}
      onWheel={handleUserScrollStart}
      onTouchStart={handleUserScrollStart}
    >
      <div className="sticky top-0 z-20 w-full bg-white shadow-sm border-b border-gray-200 mb-4">
        {/* Colored header bar for items without question number */}
        {currentItem && currentItem.content && !currentItem.content.match(/^Q\.(\d+)\s/) && (
          <div className={`${getColorForId(currentItem.refId)} h-1 w-full`}></div>
        )}
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-2">
            {/* Question number badge - only shown if there is a question number */}
            {currentItem && currentItem.content && currentItem.content.match(/^Q\.(\d+)\s/) ? (
              <div className="h-8 w-8 bg-blue-600 text-white flex items-center justify-center rounded-full font-semibold text-sm">
                {currentItem.content.match(/^Q\.(\d+)\s/)[1]}
              </div>
            ) : null}
            <div className="flex flex-col pl-1">
              <h1 className="text-xl font-semibold text-gray-800 truncate flex items-center">
                <span className="text-blue-600 mr-2">{currentItem?.refId || ''}</span>
                {currentItem?.title || ''}
              </h1>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-xs text-gray-500">
              Item {currentItem ? items.findIndex(item => item.refId === currentItem.refId) + 1 : '—'} of {items.length}
            </div>
          </div>
        </div>
        <div className="text-xs text-center text-blue-600 pb-1 opacity-75">
          <span className="inline-block animate-pulse">↓</span> Scroll to view more items <span className="inline-block animate-pulse">↓</span>
        </div>
      </div>
      
      {/* Survey items */}
      <div className="pt-2 pb-16"> 
        {items.map((item, index) => (
          <div
            key={item.refId}
            ref={el => itemRefs.current[item.refId] = el}
            data-item-id={item.refId}
            className={`p-4 mb-8 relative transition-all duration-200 ${item.refId === selectedItemId ? 'ring-2 ring-blue-500 ring-offset-2 shadow-md bg-blue-50 border-l-4 border-l-blue-500' : 'border-b-2 border-gray-200'}`}
            id={`item-${item.refId}`}
            onClick={() => {
              // When user clicks an item, we want to select it immediately
              setSelectedItemId(item.refId);
            }}
          >
            {/* Item number indicator - always visible */}
            <div className="absolute -left-3 top-4 flex flex-col items-center z-10">
              <div className="bg-gray-200 text-gray-700 text-xs px-1 py-0.5 rounded-sm mb-1 shadow-sm">
                {index + 1}/{items.length}
              </div>
              <div className="w-1 h-8 bg-gray-300 opacity-50 cursor-move hover:opacity-100 hover:bg-blue-400 rounded-full">
              </div>
            </div>
            
            {/* Selected item indicator - only shows when selected */}
            {item.refId === selectedItemId && (
              <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 w-2 h-16 bg-blue-500 rounded-r-md opacity-90 shadow-md animate-pulse"></div>
            )}
            <SurveyItemDetail
              selectedItem={item}
              updateItem={updateItem}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SurveyItemDetailView;